import type { StaticImageData } from "next/legacy/image";
import type { DefaultSeoProps, SocialProfileJsonLdProps, ArticleJsonLdProps } from "next-seo";

import * as config from ".";

// favicons (some used here, some re-exported and used elsewhere)
import faviconIco from "../../public/static/favicons/favicon.ico";
import faviconPng from "../../public/static/favicons/favicon.png";
import appleTouchIconPng from "../../public/static/favicons/apple-touch-icon.png";
import chrome512Png from "../../public/static/favicons/android-chrome-512x512.png";
import chrome192Png from "../../public/static/favicons/android-chrome-192x192.png";
import maskable512Png from "../../public/static/favicons/maskable-512x512.png";
import maskable192Png from "../../public/static/favicons/maskable-192x192.png";
import meJpg from "../../public/static/images/me.jpg";
import ogDefaultPng from "../../public/static/images/og-default.png";

// Most of this file simply takes the data already defined in ./config.js and translates it into objects that are
// compatible with next-seo's props:
// https://github.com/garmeeh/next-seo#default-seo-configuration
export const defaultSeo: DefaultSeoProps = {
  defaultTitle: `${config.siteName} – ${config.shortDescription}`,
  titleTemplate: `%s – ${config.siteName}`, // appends `– siteName` to title provided by each page (except home)
  description: config.longDescription,
  openGraph: {
    site_name: config.siteName,
    title: `${config.siteName} – ${config.shortDescription}`,
    locale: config.siteLocale?.replace("-", "_"),
    type: "website",
  },
  additionalMetaTags: [
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1",
    },
    {
      name: "author",
      content: config.authorName,
    },
  ],
  additionalLinkTags: [
    {
      rel: "icon",
      href: faviconIco.src,
    },
    {
      rel: "icon",
      href: faviconPng.src,
      type: "image/png",
    },
    {
      rel: "apple-touch-icon",
      href: appleTouchIconPng.src,
      sizes: `${appleTouchIconPng.width}x${appleTouchIconPng.height}`,
    },
    {
      rel: "manifest",
      href: "/site.webmanifest",
    },
    {
      rel: "alternate",
      href: "/feed.xml",
      type: "application/rss+xml",
      // @ts-ignore
      title: `${config.siteName} (RSS)`,
    },
    {
      rel: "alternate",
      href: "/feed.atom",
      type: "application/atom+xml",
      // @ts-ignore
      title: `${config.siteName} (Atom)`,
    },
    {
      rel: "webmention",
      href: `https://webmention.io/${config.webmentionId}/webmention`,
    },
    {
      rel: "pingback",
      href: `https://webmention.io/${config.webmentionId}/xmlrpc`,
    },
    {
      rel: "humans",
      href: "/humans.txt",
    },
    {
      rel: "pgpkey",
      href: "/pubkey.asc",
      type: "application/pgp-keys",
    },
  ],
};

// https://github.com/garmeeh/next-seo#social-profile
export const socialProfileJsonLd: SocialProfileJsonLdProps = {
  type: "Person",
  name: config.authorName,
  url: `${config.baseUrl}/`,
  sameAs: [
    `${config.baseUrl}/`,
    `https://github.com/${config.authorSocial?.github}`,
    `https://www.linkedin.com/in/${config.authorSocial?.linkedin}/`,
  ],
};

// Just the basic items applicable to all blog posts, extended by pages/blog/[slug].tsx
// https://github.com/garmeeh/next-seo#article-1
export const articleJsonLd: Pick<ArticleJsonLdProps, "authorName" | "publisherName" | "publisherLogo"> = {
  authorName: [config.authorName],
  publisherName: config.siteName,
  publisherLogo: `${config.baseUrl}${meJpg.src}`,
};

// Re-export icons to use their static image data elsewhere
export const favicons: Record<string, StaticImageData> = {
  faviconIco,
  faviconPng,
  appleTouchIconPng,
  chrome512Png,
  chrome192Png,
  maskable512Png,
  maskable192Png,
  meJpg,
  ogDefaultPng,
};
